import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shadcn/ui/tabs";
import {
  findCountryCodeGivenCountryName,
  findCountryNameGivenCountryCode,
} from "../common/nearby-shows/utils";

import { Button } from "@/shadcn/ui/button";
import CitySearchFields from "@/components/common/CitySearchFields";
import { ComboBoxResponsive } from "@/components/common/ComboBoxResponsive";
import { LocationSettingsModalOrDrawer } from "./LocationSettingsModalOrDrawer";
import { Slider } from "@/shadcn/ui/slider";
import { UpdateLocationFormValues } from "./types";
import { supportedCountries } from "../common/phone-input/countries";

const MIN_RADIUS = 0; // miles
const MAX_RADIUS = 100; // miles
const STEP_SIZE = 5; // miles
const MARKER_STEP_SIZE = 10; // only show the slider markers every 10 miles

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleUpdate: (updatedValues: Partial<UpdateLocationFormValues>) => void;
  formValues: UpdateLocationFormValues;
  setFormValues: (values: UpdateLocationFormValues) => void;
  countryCode: string | null;
  detectedCountryCode: string | null;
}

// Function to create country dropdown options
const COUNTRIES_DROPDOWN_OPTIONS = supportedCountries
  .sort((a, b) => (a.name > b.name ? 1 : -1))
  .map((country) => ({
    label: `${country.flag} ${country.name}`,
    shortLabel: `${country.flag} ${country.name}`,
    value: country.name,
  }));

export default function ShowsNearbyChangeLocationModal({
  isOpen,
  setIsOpen,
  handleSubmit,
  handleUpdate,
  formValues,
  setFormValues,
  countryCode,
  detectedCountryCode,
}: Props) {
  return (
    <LocationSettingsModalOrDrawer isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">Location Settings</h2>
        <p className="text-base text-neutral-600">
          Set your location and search radius to find shows near you.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
        className="flex flex-col gap-8"
      >
        <div className="space-y-4">
          <Tabs
            defaultValue="city"
            value={formValues.searchType}
            onValueChange={(value) =>
              setFormValues({
                ...formValues,
                searchType: value as "city" | "postalCode",
              })
            }
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="city">City Search</TabsTrigger>
              <TabsTrigger value="postalCode">Postal Code</TabsTrigger>
            </TabsList>

            <TabsContent value="city" className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <CitySearchFields
                  city={formValues.city}
                  onCityChange={(city: string) => handleUpdate({ city })}
                  defaultCountryCode={
                    countryCode ?? detectedCountryCode ?? null
                  }
                  onCountryCodeChange={(countryCode: string) =>
                    handleUpdate({
                      countryCode,
                    })
                  }
                />
              </div>
            </TabsContent>

            <TabsContent value="postalCode" className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-col">
                  <label
                    htmlFor="postalCode"
                    className="font-sans font-semibold text-[16px] leading-7 mb-1"
                  >
                    Postal Code
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    placeholder="Enter postal code"
                    defaultValue={formValues.postalCode}
                    onChange={(e) =>
                      handleUpdate({
                        postalCode: e.target.value,
                      })
                    }
                    className="w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue rounded-[10px] px-4 py-3"
                  />
                </div>
                <div className="flex flex-col mt-2">
                  <label
                    htmlFor="country"
                    className="font-sans font-semibold text-[16px] leading-7 mb-1"
                  >
                    Country
                  </label>
                  <ComboBoxResponsive
                    options={COUNTRIES_DROPDOWN_OPTIONS}
                    value={findCountryNameGivenCountryCode(
                      formValues.countryCode
                    )}
                    setValue={(value: string) =>
                      handleUpdate({
                        countryCode: findCountryCodeGivenCountryName(value),
                      })
                    }
                    label="country"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="relative flex flex-col gap-1.5">
            <div className="flex justify-between items-center">
              <span className="font-sans font-semibold text-[16px] leading-7">
                Radius
              </span>
              <span className="text-sm font-medium text-neutral-600">
                {formValues.radius} miles
              </span>
            </div>
            <Slider
              value={[formValues.radius]}
              onValueChange={(value) =>
                setFormValues({ ...formValues, radius: value[0] })
              }
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              step={STEP_SIZE}
              showMarkers
              markerStepSize={MARKER_STEP_SIZE}
              className="pt-2"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-6"
        >
          Save Changes
        </Button>
      </form>
    </LocationSettingsModalOrDrawer>
  );
}
